import { collection, getDocs, query, doc, serverTimestamp, updateDoc, where, orderBy, addDoc } from "firebase/firestore";

import { firestore } from "./FirebaseService";
import { ReminderState, ReminderType } from "../config/DiactiveTypes";

const usersCollectionName = "users";
const remindersCollectionName = "reminders";

const hiddenReminderTypes = [
  ReminderType.FillQuestionnairesPre,
  ReminderType.FillQuestionnairesPost,
  ReminderType.UploadGlucoseFilePreFollowUp,
  ReminderType.UploadGlucoseFilePostFollowUp
];

const mapReminderDoc = (docSnap, patientId = null) => {
  const data = docSnap.data();
  const reminder = {
    id: docSnap.id,
    activatedAtLocaleString: data.activatedAt?.toDate ? data.activatedAt.toDate().toLocaleDateString("es-ES") : null,
    ...data,
  };
  if (patientId != null) reminder.patient = patientId;
  return reminder;
};

/**
 * Fetches reminders for multiple patients (e.g. all of a doctor's patients).
 * Returns reminders with a `patient` field set to the patient id for each.
 */
export const getDoctorReminders = async (patientIds) => {
  if (!patientIds || patientIds.length === 0) return [];

  const allReminders = [];
  for (const patientId of patientIds) {
    const remindersRef = collection(firestore, usersCollectionName, patientId, remindersCollectionName);
    const remindersQuery = query(
      remindersRef,
      where("state", "==", ReminderState.Active),
      where("type", "not-in", hiddenReminderTypes),
      orderBy("activatedAt", "desc")
    );
    const querySnapshot = await getDocs(remindersQuery);
    querySnapshot.docs.forEach((docSnap) => {
      allReminders.push(mapReminderDoc(docSnap, patientId));
    });
  }
  allReminders.sort((a, b) => {
    const aAt = a.activatedAt?.toDate?.()?.getTime?.() ?? 0;
    const bAt = b.activatedAt?.toDate?.()?.getTime?.() ?? 0;
    return bAt - aAt;
  });
  return allReminders;
};

/**
 * Fetches reminders for a single patient (under users/{patientId}/reminders).
 */
export const getPatientReminders = async (patientId) => {
  const remindersRef = collection(firestore, usersCollectionName, patientId, remindersCollectionName);
  const remindersQuery = query(
    remindersRef,
    where("state", "==", ReminderState.Active),
    where("type", "not-in", hiddenReminderTypes),
    orderBy("activatedAt", "desc")
  );
  const querySnapshot = await getDocs(remindersQuery);
  return querySnapshot.docs.map((docSnap) => mapReminderDoc(docSnap));
};

export const createReminder = async (patientId, type) => {
  const remindersRef = collection(firestore, usersCollectionName, patientId, remindersCollectionName);
  const newReminder = {
    type,
    state: ReminderState.Inactive,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    activatedAt: null,
    completedAt: null
  };
  const docRef = await addDoc(remindersRef, newReminder);
  return docRef.id;
};

export const updateReminderStatus = async (patientId, reminderId, completed) => {
  const docRef = doc(firestore, usersCollectionName, patientId, remindersCollectionName, reminderId);
  await updateDoc(docRef, {
    state: completed ? ReminderState.Completed : ReminderState.Active,
    completedAt: completed ? serverTimestamp() : null,
    updatedAt: serverTimestamp()
  });
};

export const reactivateReminder = async (patientId, reminderType) => {
  const remindersRef = collection(firestore, usersCollectionName, patientId, remindersCollectionName);
  const remindersQuery = query(
    remindersRef,
    where("type", "==", reminderType)
  );
  const querySnapshot = await getDocs(remindersQuery);

  const updatePromises = querySnapshot.docs.map((docSnap) => {
    const docRef = doc(firestore, usersCollectionName, patientId, remindersCollectionName, docSnap.id);
    return updateDoc(docRef, {
      state: ReminderState.Active,
      completedAt: null,
      updatedAt: serverTimestamp()
    });
  });

  await Promise.all(updatePromises);
};
